import { useGlobalQuery } from 'state/query-client/hooks';
import { getIntegrations, IntegrationProviders } from 'services/integration-service';
import { useConfig } from './use-config';
import { useIntegrationsStore } from 'state/store/integrations/useIntegrationsStore';
import { useEffect } from 'react';

/**
 * Custom hook to fetch user integrations
 *
 * This hook retrieves the list of active integrations for the current user.
 * It uses the global query pattern for consistent error handling and caching.
 *
 * Features:
 * - Automatic configuration injection
 * - Global error handling with authentication management
 * - Caching and background refetching
 * - Loading and error state management
 * - TypeScript support for integration data
 *
 * @returns {Object} The query result object
 * @returns {IntegrationProviders[] | undefined} data - Array of user integrations
 * @returns {boolean} isLoading - Whether the query is loading
 * @returns {boolean} isError - Whether the query failed
 * @returns {boolean} isSuccess - Whether the query completed successfully
 * @returns {Error | null} error - The error if query failed
 * @returns {Function} refetch - Function to manually refetch the data
 *
 * @example
 * const { data: integrations, isLoading, refetch } = useGetIntegrationsProviders();
 * 
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 * 
 * const googleDriveIntegration = integrations?.find(
 *   integration => integration.integration_type === 'google-drive'
 * );
 * 
 * return (
 *   <div>
 *     {googleDriveIntegration ? (
 *       <ConnectedState integration={googleDriveIntegration} />
 *     ) : (
 *       <DisconnectedState />
 *     )}
 *   </div>
 * );
 */
export const useGetIntegrationsProviders = () => {
    const { configurationSettings } = useConfig();
    const {
        integrations,
        isLoading: storeLoading,
        error: storeError,
        setIntegrations,
        setLoading,
        setError,
        lastFetchTime
    } = useIntegrationsStore();

    // Determine if we should fetch fresh data (every 30 seconds or if no data)
    const shouldFetch = !lastFetchTime || 
                       (Date.now() - lastFetchTime > 30000) || 
                       integrations.length === 0;

    const queryResult = useGlobalQuery<IntegrationProviders[]>({
        queryKey: ['getIntegrations'],
        queryFn: async () => {
            if (!configurationSettings) {
                throw new Error('Configuration settings are not available');
            }
            setLoading(true);
            try {
                const result = await getIntegrations(configurationSettings);
                setIntegrations(result);
                setError(null);
                return result;
            } catch (error) {
                setError(error instanceof Error ? error.message : 'Failed to fetch integrations');
                throw error;
            } finally {
                setLoading(false);
            }
        },
        enabled: !!configurationSettings && shouldFetch,
        staleTime: 30000, // 30 seconds
        refetchOnMount: shouldFetch,
        refetchOnWindowFocus: false,
    });

    // Sync loading and error states
    useEffect(() => {
        if (queryResult.isLoading !== storeLoading) {
            setLoading(queryResult.isLoading);
        }
    }, [queryResult.isLoading, storeLoading, setLoading]);

    useEffect(() => {
        if (queryResult.isSuccess) {
            setError(null);
            return;
        }

        if (queryResult.error && !storeError) {
            setError(queryResult.error instanceof Error ? queryResult.error.message : 'Query failed');
        }
    }, [queryResult.isSuccess, queryResult.error, storeError, setError]);

    const hasCachedData = integrations.length > 0;
    const hasFetchError = !!storeError || queryResult.isError;

    // Prefer cached store data for display; only surface error/loading when there is nothing to show
    return {
        data: integrations,
        isLoading: (storeLoading || queryResult.isLoading) && !hasCachedData,
        error: storeError || queryResult.error,
        isError: hasFetchError && !hasCachedData,
        isSuccess: !hasFetchError || hasCachedData,
        refetch: queryResult.refetch,
    };
};
